import React from 'react';
import { useTranslations, useLocale } from '@unisporkal/localization';
import { useAsset } from '../../hooks/stateful/useAsset';
import styles from './SchemaMetaData.module.scss';
import formatPrice from '../../support/formatPrice';

const ProductSchema = ({ size }) => {
  const { asset } = useAsset();
  const { isImage, isIllustration, isVector } = asset;
  const currencyCode = size?.[0]?.currencyCode || '';
  const getPrice = (index: number) => {
    const price = size?.[index]?.price;
    return price ? formatPrice(price).replace(/\s+/g, '') : '';
  };
  const highPrice = getPrice(2);
  const lowPrice = getPrice(0);
  const imageUrl = asset.imageUrls?.image612;
  const locale = useLocale();

  const t = useTranslations();
  const illustrationTranslation = (): string => t('stock_illustration');
  const photoTranslation = (): string => t('stock_photo');

  const formatTitle = (title?: string): string => {
    if (!title) return '';

    const regex = /\(.*\)|[^a-zA-Z0-9\s]|_/g;
    return title
      .replace(/\//g, ' ')
      .replace(/,/g, ' ')
      .replace(regex, '')
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace(/\s+/g, ' ');
  };

  const mediaDescription = (): string => {
    switch (asset.mediaType) {
      case 'photography':
      case 'photo':
        return photoTranslation();
      case 'illustration':
      case 'photo_and_illustration':
      case 'photography_and_illustration':
        return illustrationTranslation();
      default:
        return photoTranslation();
    }
  };

  const sanitizedEditorialTitle = (text) => {
    if (!text) return '';
    if (text.length <= 70) return text;

    let count = 0;
    const words = text.split(' ');
    const truncatedWords = words.filter((word) => {
      count += word.length + 1;
      return count <= 70;
    });

    return `${truncatedWords.join(' ')}...`;
  };

  const seoMetaDescription = (): string => {
    if (asset.isEditorial()) {
      return `${sanitizedEditorialTitle(asset.title)} ${t(
        'get_premium_news_photos',
        { site_name: 'Getty Images' }
      )}`;
    }
    if (isIllustration || isVector) {
      return t('seo_meta_description_stock_illustrations', {
        asset_title: asset.title,
      });
    }
    return t('seo_meta_description_stock_photos', { asset_title: asset.title });
  };

  const seoTitle = (): string => {
    if (asset.isEditorial()) {
      return `${sanitizedEditorialTitle(asset.title)}, ${t('news_photo')}`;
    }
    if (locale.includes('en')) {
      if (isIllustration) {
        return t('high_res_stock_vector', {
          asset_title: formatTitle(asset.title),
        });
      }
      if (isImage()) {
        return t('high_res_stock_photo', {
          asset_title: formatTitle(asset.title),
        });
      }
    }
    return `${formatTitle(asset.title)}, ${mediaDescription()}`;
  };

  return (
    <div
      data-testid="product-schema"
      className={styles.schema}
      itemScope
      itemType="http://schema.org/Product"
    >
      <span itemProp="brand">Getty Images</span>
      <span itemProp="name">{seoTitle()}</span>
      <img
        itemProp={asset.type}
        src={imageUrl}
        alt={seoTitle()}
      />
      <span itemProp="description">{seoMetaDescription()}</span>
      <span>Product #:</span>
      <span itemProp="mpn">{asset.assetId}</span>
      <div
        itemProp="offers"
        itemScope
        itemType="http://schema.org/AggregateOffer"
      >
        <span
          itemProp="priceCurrency"
          content={currencyCode}
        />
        <span
          itemProp="highPrice"
          content={highPrice}
        >
          {highPrice}
        </span>
        <span
          itemProp="lowPrice"
          content={lowPrice}
        >
          {lowPrice}
        </span>
        <div
          itemProp="seller"
          itemScope
          itemType="http://schema.org/Organization"
        >
          <span itemProp="name">Getty Images</span>
        </div>
        <link
          itemProp="availability"
          href="http://schema.org/InStock"
        />
        <span>In stock</span>
      </div>
    </div>
  );
};

export default ProductSchema;
